// B-1026 · LA COLA DE ENVÍOS DE ESTE DISPOSITIVO, una sola para toda la app. El motor (qué se guarda, cuándo se
// reintenta, qué se hace con cada respuesta) está en utils/colaEnvios.js; aquí se le da lo del navegador: IndexedDB,
// la red de la app (services/api, con su sesión y su clínica), la sesión de AHORA y el reloj de los reintentos.
import api, { clinicaDeSesion } from './api'
import { crearCola, almacenIndexedDB, almacenEnMemoria, conectarCola } from '../utils/colaEnvios'
import { cuentaDeEstaPestana } from '../utils/cuentasDelDispositivo'
import { t } from '../utils/i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// LA SESIÓN DE AHORA, leída en cada envío (no una vez al cargar): si se cambia de clínica o de cuenta, aunque sea en
// otra pestaña, el siguiente intento ya lo sabe. La clínica es la MISMA que decide la cabecera de cada petición
// (services/api · clinicaDeSesion), no una copia del criterio.
function sesionActual() {
  try {
    if (!localStorage.getItem('token')) return null
    const u = JSON.parse(localStorage.getItem('user') || 'null')
    // t548 · la cuenta y la clínica de ESTA pestaña (services/api · clinicaDeSesion): si otra pestaña ha cambiado de clínica
    // o de cuenta, lo de aquí se apunta (y se manda) a nombre de la de esta, nunca de la otra.
    const usuarioId = parseInt(cuentaDeEstaPestana(), 10) || parseInt(u && u.id, 10)
    const clinicaId = parseInt(clinicaDeSesion(), 10)
    return usuarioId > 0 && clinicaId > 0 ? { usuarioId, clinicaId } : null
  } catch (_) { return null }
}

// Sin respuesta (red o servidor caídos) LANZA; con respuesta, la devuelve tal cual, sea la que sea.
async function red({ metodo, url, json, archivo, campos, cabeceras }) {
  let data
  const headers = { ...(cabeceras || {}) }
  if (archivo && archivo.blob) {
    const fd = new FormData()
    for (const [k, v] of Object.entries(campos || {})) fd.append(k, v)
    fd.append(archivo.campo || 'audio', archivo.blob, archivo.nombre || 'audio.webm')
    data = fd
    headers['Content-Type'] = 'multipart/form-data'
  } else {
    data = json || undefined
  }
  try {
    const r = await api.request({ method: metodo, url, data, headers })
    return { status: r.status, data: r.data }
  } catch (e) {
    if (e && e.response) return { status: e.response.status, data: e.response.data }
    // t548 · parado en el navegador porque esta pestaña es de otra clínica: lo mismo que el 409 del servidor, que la cola guarda
    // para la sesión de su clínica (`otra_sesion`) en vez de reintentarlo como si no hubiera red.
    if (e && e.esPestanaDeOtraClinica) return { status: 409, data: { motivo: 'otra_sesion', codigo: e.codigo, error: e.message } }
    throw e
  }
}

function uuid() {
  try { if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID() } catch (_) { /* sigue */ }
  const b = new Uint8Array(16)
  window.crypto.getRandomValues(b)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20)
}

// IndexedDB si se deja; si no (o falla al usarlo), en memoria: sigue reintentando mientras la pestaña esté abierta.
function almacenConRespaldo() {
  const memoria = almacenEnMemoria()
  let idb = null
  try { idb = almacenIndexedDB(typeof window !== 'undefined' ? window.indexedDB : null) } catch (_) { idb = null }
  let usar = idb || memoria
  const con = (op) => async (...a) => {
    try { return await usar[op](...a) } catch (_) { usar = memoria; return memoria[op](...a) }
  }
  return {
    get persistente() { return usar !== memoria },
    todas: con('todas'), poner: con('poner'), quitar: con('quitar'),
  }
}

export const cola = crearCola({
  almacen: almacenConRespaldo(),
  red,
  sesion: sesionActual,
  uuid,
  // Una sola pestaña reparte la cola a la vez; el servidor no repite aunque dos lo intentaran (X-Envio-Id).
  bloqueo: (typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function')
    ? (fn) => navigator.locks.request('biango-cola-envios', fn)
    : null,
})

// EL PUENTE CON LAS PANTALLAS, puesto al CARGAR este módulo (App.jsx lo importa al arrancar): cuando una pantalla
// manda algo (utils/colaEnvios · enviarConCola), la cola ya está escuchando.
conectarCola(cola)

// t545 · LAS MEDIDAS DE UN PERIODONTOGRAMA QUE YA NO EXISTE, A UN ESTUDIO NUEVO DEL MISMO PACIENTE. El servidor decide de
// qué paciente era (la copia de la papelera de ESTA clínica, o el paciente que apuntó la pantalla al medir) y comprueba que
// es de esta clínica y que quien lo pide lo puede ver (routes/periodonto · POST /:id/rescatar). Va con el identificador del
// envío (X-Envio-Id): si la respuesta se pierde y se pulsa otra vez, no se crean dos estudios. Si llega, sale de la cola.
export async function rescatarMedidas(id) {
  const m = cola.medidasDe(id)
  const est = m && /^\/periodonto\/(\d+)$/.exec(String(m.url || ''))
  if (!m || !est) return { ok: false, error: t('services.colaEnvios.estas_medidas_ya_no_estan', 'Estas medidas ya no están en este ordenador.') }
  let r
  try {
    r = await red({
      metodo: 'POST', url: '/periodonto/' + est[1] + '/rescatar',
      json: { datos: m.datos, ...(m.paciente_id ? { paciente_id: m.paciente_id } : {}) },
      cabeceras: { 'X-Envio-Id': m.id, 'X-Envio-Clinica': String(m.clinicaId), 'X-Envio-Usuario': String(m.usuarioId) },
    })
  } catch (_) {
    return { ok: false, error: t('services.colaEnvios.no_hay_conexion_con_el', 'No hay conexión con el servidor: vuelve a intentarlo en un momento. Las medidas siguen guardadas aquí.') }
  }
  const d = r.data && typeof r.data === 'object' ? r.data : {}
  if (r.status >= 200 && r.status < 300) { await cola.descartar(id); return { ok: true, ...d } }
  // El estudio vuelve a existir (se recuperó de la papelera): las medidas van a él, como un reintento a mano.
  if (r.status === 409 && d.motivo === 'existe') { await cola.reintentar(id); return { ok: true, reintentado: true } }
  return { ok: false, status: r.status, motivo: d.motivo || null, error: d.error || t('services.colaEnvios.no_se_ha_podido_guardar', 'No se ha podido guardar en un estudio nuevo. Las medidas siguen guardadas aquí.') }
}

let _arrancada = false
/** Pone en marcha los reintentos (una vez por pestaña): cada 15 s, al volver la red y al volver a la pestaña. */
export function arrancarCola() {
  if (_arrancada || typeof window === 'undefined') return
  _arrancada = true
  const vuelta = () => { cola.procesar() }
  vuelta()
  setInterval(vuelta, 15000)
  window.addEventListener('online', vuelta)
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') vuelta() })
  // Otra pestaña entra o sale (token, usuario, la visita del soporte): el aviso se repinta ya.
  window.addEventListener('storage', () => { cola.recargar().catch(() => {}) })
}
